import type { User } from "oidc-client-ts";

const defaultApiBaseUrl =
  import.meta.env?.VITE_EVOBUDDY_API_BASE_URL ?? "https://api.evobuddy.com";

export type SessionSummary = {
  tenant_id: string;
  session_id: string;
  title?: string | null;
  created_by_principal_id?: string | null;
  created_at: string;
  updated_at: string;
  last_message_at?: string | null;
  last_message_preview?: string | null;
  archived: boolean;
  read_cursor_message_id?: string | null;
  unread_count: number;
};

export type MessageEnvelope = {
  tenant_id: string;
  session_id: string;
  message_id: string;
  direction: "inbound" | "outbound";
  sender_kind: "user" | "agent" | "system" | "service";
  content: string;
  content_type: "text_plain" | "text_markdown";
  correlation_id: string;
  causation_id?: string | null;
  policy_version: string;
  created_at: string;
};

export type MessageContentType = MessageEnvelope["content_type"];

export type SendMessageResponse = {
  message_id: string;
  decision: string;
};

export type CreateSessionRequest = {
  title?: string;
  initial_message?: {
    content: string;
    content_type: MessageContentType;
  };
};

export type CreateSessionResponse = {
  session: SessionSummary;
  initial_message?: SendMessageResponse | null;
};

export type NotificationEvent = {
  notification_id: string;
  tenant_id: string;
  session_id: string;
  message_id: string;
  event_kind: "message_persisted" | "policy_event_persisted";
  created_at: string;
};

export type SessionMessagesEvent = {
  type: "message";
  notification: NotificationEvent;
  messages: MessageEnvelope[];
};

export type SessionReadyEvent = {
  type: "ready";
  session_id: string;
};

export type SessionEvent = SessionMessagesEvent | SessionReadyEvent;

export type SessionEventHandlers = {
  onEvent: (event: SessionEvent) => void;
  onError?: (error: BffClientError) => void;
};

export type SessionEventConnection = {
  close: () => void;
};

export type FetchImpl = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

type RequestOptions = {
  method?: "GET" | "POST";
  body?: unknown;
};

export type BffClientOptions = {
  apiBaseUrl?: string;
  fetchImpl?: FetchImpl;
};

export type SessionEventOptions = {
  reconnect?: boolean;
  reconnectDelayMs?: number;
};

type RawSseEvent = {
  id?: string;
  event?: string;
  data: string;
};

type SessionMessagesEventPayload = {
  notification: NotificationEvent;
  messages: MessageEnvelope[];
};

export class AuthRecoveryRequiredError extends Error {
  readonly name = "AuthRecoveryRequiredError";
  readonly kind = "auth-recovery";
  readonly status = 401;
  readonly shouldRecoverAuth = true;

  constructor(message = "Authentication required") {
    super(message);
  }
}

export class AuthorizationError extends Error {
  readonly name = "AuthorizationError";
  readonly kind = "authorization";
  readonly status = 403;

  constructor(message = "Access denied") {
    super(message);
  }
}

export class BffHttpError extends Error {
  readonly name = "BffHttpError";
  readonly kind = "http";

  constructor(
    readonly status: number,
    message = `BFF request failed with HTTP ${status}`,
  ) {
    super(message);
  }
}

export class BffNetworkError extends Error {
  readonly name = "BffNetworkError";
  readonly kind = "network";

  constructor(message: string) {
    super(message);
  }
}

export type BffClientError =
  | AuthRecoveryRequiredError
  | AuthorizationError
  | BffHttpError
  | BffNetworkError;

export class BffClient {
  private readonly apiBaseUrl: string;
  private readonly fetchImpl: FetchImpl;

  constructor(
    private readonly user: Pick<User, "access_token">,
    options: BffClientOptions = {},
  ) {
    this.apiBaseUrl = options.apiBaseUrl ?? defaultApiBaseUrl;
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }

  listSessions(): Promise<SessionSummary[]> {
    return this.request("/v1/agent-sessions");
  }

  createSession(request: CreateSessionRequest = {}): Promise<CreateSessionResponse> {
    return this.request("/v1/agent-sessions", {
      method: "POST",
      body: request,
    });
  }

  fetchSessionMessages(sessionId: string): Promise<MessageEnvelope[]> {
    return this.request(`/v1/agent-sessions/${encodeURIComponent(sessionId)}/messages`);
  }

  listMessages(sessionId: string): Promise<MessageEnvelope[]> {
    return this.fetchSessionMessages(sessionId);
  }

  sendMessage(
    sessionId: string,
    content: string,
    contentType: MessageContentType = "text_plain",
  ): Promise<SendMessageResponse> {
    return this.request(`/v1/agent-sessions/${encodeURIComponent(sessionId)}/messages`, {
      method: "POST",
      body: {
        content,
        content_type: contentType,
      },
    });
  }

  connectSessionEvents(
    sessionId: string,
    handlers: SessionEventHandlers,
    options: SessionEventOptions = {},
  ): SessionEventConnection {
    const controller = new AbortController();
    const seenNotifications = new Set<string>();
    let closed = false;
    let activeReader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let resolveReconnect: (() => void) | undefined;
    const reconnect = options.reconnect ?? true;
    const reconnectDelayMs = options.reconnectDelayMs ?? 1_000;

    const close = () => {
      closed = true;
      controller.abort();
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      resolveReconnect?.();
      void activeReader?.cancel();
    };

    const run = async (): Promise<void> => {
      while (!closed) {
        try {
          await this.readSessionEvents(sessionId, controller.signal, (reader) => {
            activeReader = reader;
          }, (rawEvent) => {
            const event = parseSessionEvent(rawEvent);
            if (!event) {
              return;
            }
            if (event.type === "message") {
              const id = event.notification.notification_id;
              if (seenNotifications.has(id)) {
                return;
              }
              seenNotifications.add(id);
            }
            handlers.onEvent(event);
          });
        } catch (error) {
          if (closed || isAbortError(error)) {
            return;
          }
          handlers.onError?.(toBffClientError(error));
        } finally {
          activeReader = undefined;
        }

        if (!reconnect || closed) {
          return;
        }

        await new Promise<void>((resolve) => {
          resolveReconnect = resolve;
          reconnectTimer = setTimeout(resolve, reconnectDelayMs);
        });
        reconnectTimer = undefined;
        resolveReconnect = undefined;
      }
    };

    void run();
    return { close };
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    let response: Response;
    try {
      response = await this.fetchImpl(urlFor(this.apiBaseUrl, path), {
        method: options.method ?? "GET",
        headers: this.headers(options.body),
        body: options.body ? JSON.stringify(options.body) : undefined,
      });
    } catch (error) {
      throw new BffNetworkError(errorMessage(error));
    }

    if (!response.ok) {
      throw await errorFromResponse(response);
    }

    return response.json() as Promise<T>;
  }

  private async readSessionEvents(
    sessionId: string,
    signal: AbortSignal,
    onReader: (reader: ReadableStreamDefaultReader<Uint8Array>) => void,
    onRawEvent: (event: RawSseEvent) => void,
  ): Promise<void> {
    let response: Response;
    try {
      response = await this.fetchImpl(
        urlFor(this.apiBaseUrl, `/v1/agent-sessions/${encodeURIComponent(sessionId)}/events`),
        {
          method: "GET",
          headers: this.headers(undefined, "text/event-stream"),
          signal,
        },
      );
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
      throw new BffNetworkError(errorMessage(error));
    }

    if (!response.ok) {
      throw await errorFromResponse(response);
    }
    if (!response.body) {
      throw new BffNetworkError("BFF event stream response had no body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const parser = new SseParser();
    onReader(reader);

    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      for (const event of parser.push(decoder.decode(value, { stream: true }))) {
        onRawEvent(event);
      }
    }
  }

  private headers(body?: unknown, accept = "application/json"): HeadersInit {
    return {
      Accept: accept,
      Authorization: `Bearer ${this.user.access_token}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    };
  }
}

export function mergeSessionMessages(
  current: readonly MessageEnvelope[],
  incoming: readonly MessageEnvelope[],
): MessageEnvelope[] {
  const byId = new Map<string, MessageEnvelope>();
  for (const message of [...current, ...incoming]) {
    byId.set(message.message_id, message);
  }

  return [...byId.values()].sort(compareMessages);
}

class SseParser {
  private buffer = "";

  push(chunk: string): RawSseEvent[] {
    this.buffer += chunk.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const events: RawSseEvent[] = [];
    let boundary = this.buffer.indexOf("\n\n");

    while (boundary >= 0) {
      const rawEvent = this.buffer.slice(0, boundary);
      this.buffer = this.buffer.slice(boundary + 2);
      const event = parseRawSseEvent(rawEvent);
      if (event) {
        events.push(event);
      }
      boundary = this.buffer.indexOf("\n\n");
    }

    return events;
  }
}

function parseRawSseEvent(rawEvent: string): RawSseEvent | undefined {
  let id: string | undefined;
  let event: string | undefined;
  const data: string[] = [];

  for (const line of rawEvent.split("\n")) {
    if (!line || line.startsWith(":")) {
      continue;
    }
    const separator = line.indexOf(":");
    const field = separator >= 0 ? line.slice(0, separator) : line;
    const value = separator >= 0 ? trimSseValue(line.slice(separator + 1)) : "";

    if (field === "id") {
      id = value;
    } else if (field === "event") {
      event = value;
    } else if (field === "data") {
      data.push(value);
    }
  }

  if (!event && data.length === 0) {
    return undefined;
  }

  return { id, event, data: data.join("\n") };
}

function parseSessionEvent(rawEvent: RawSseEvent): SessionEvent | undefined {
  if (rawEvent.event === "ready") {
    return {
      type: "ready",
      session_id: rawEvent.data.startsWith("session:") ? rawEvent.data.slice(8) : rawEvent.data,
    };
  }

  if (rawEvent.event === "message") {
    const payload = JSON.parse(rawEvent.data) as SessionMessagesEventPayload;
    return {
      type: "message",
      notification: payload.notification,
      messages: payload.messages,
    };
  }

  return undefined;
}

async function errorFromResponse(response: Response): Promise<BffClientError> {
  const message = await errorMessageFromResponse(response);
  if (response.status === 401) {
    return new AuthRecoveryRequiredError(message);
  }
  if (response.status === 403) {
    return new AuthorizationError(message);
  }
  return new BffHttpError(response.status, message);
}

async function errorMessageFromResponse(response: Response): Promise<string> {
  try {
    const payload = (await response.clone().json()) as { error?: string };
    if (payload.error) {
      return payload.error;
    }
  } catch {
    // Fall through to the generic status message.
  }
  return `BFF request failed with HTTP ${response.status}`;
}

function toBffClientError(error: unknown): BffClientError {
  if (
    error instanceof AuthRecoveryRequiredError ||
    error instanceof AuthorizationError ||
    error instanceof BffHttpError ||
    error instanceof BffNetworkError
  ) {
    return error;
  }

  return new BffNetworkError(errorMessage(error));
}

function compareMessages(left: MessageEnvelope, right: MessageEnvelope): number {
  const leftTime = Date.parse(left.created_at);
  const rightTime = Date.parse(right.created_at);
  if (leftTime !== rightTime) {
    return leftTime - rightTime;
  }
  return left.message_id.localeCompare(right.message_id);
}

function urlFor(apiBaseUrl: string, path: string): string {
  return `${apiBaseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

function trimSseValue(value: string): string {
  return value.startsWith(" ") ? value.slice(1) : value;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "BFF request failed";
}
